import { Button } from '@strata/react-buttons';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelect } from '@strata/react-selects';
import PropTypes from 'prop-types';

import { handleError } from 'services/ErrorManagement';
import { emptyOnNull } from 'services/Helpers';
import { get } from 'services/Http';
import { ReferralAssignmentCommPreference } from 'services/NPISupport';

const ReferralAssignmentCommPreferenceDisplayTextMap = {
    [ReferralAssignmentCommPreference.FAX]: 'Fax',
    [ReferralAssignmentCommPreference.NO_CONTACT]: 'Do not contact',
};

const ReferralAssignmentCommPreferenceOptions = Object.values(ReferralAssignmentCommPreference).map(
    (value) => ({
        value,
        name: ReferralAssignmentCommPreferenceDisplayTextMap[value],
    }),
);

export const PrescriberDetailsFormFields = ({ formik, isCreate, isReadOnly, hasPracticeNpi }) => (
    <>
        <TextInputFormik
            label="Prescriber NPI"
            id="npi"
            required
            disabled={!isCreate || isReadOnly}
        />
        {isCreate && (
            <Button
                className="margin__bottom--medium"
                label="Lookup NPI"
                onClick={async () => {
                    try {
                        const data = await get(`/reference/npis/${formik.values.npi}`);
                        formik.setValues({
                            ...formik.values,
                            organization_name: emptyOnNull(data.organization_name),
                            first_name: emptyOnNull(data.first_name),
                            last_name: emptyOnNull(data.last_name),
                            address_1: emptyOnNull(data.address_1),
                            address_2: emptyOnNull(data.address_2),
                            city: emptyOnNull(data.city),
                            state: emptyOnNull(data.state),
                            postal_code: emptyOnNull(data.postal_code),
                            telephone_number: emptyOnNull(data.telephone_number),
                            fax_number: emptyOnNull(data.fax_number),
                            taxonomy_code: data.taxonomy_code,
                            classification: data.classification,
                            specialization: emptyOnNull(data.specialization),
                        });
                    } catch (err) {
                        handleError('NPI lookup error.', err);
                    }
                }}
            />
        )}
        <TextInputFormik
            label="Practice NPI"
            id="practice_npi"
            disabled={hasPracticeNpi || isReadOnly}
        />
        <Button
            className="margin__bottom--medium"
            label="Lookup Practice NPI"
            onClick={async () => {
                try {
                    const data = await get(`/reference/npis/${formik.values.practice_npi}`);

                    await formik.setFieldValue(
                        'organization_name',
                        emptyOnNull(data.organization_name),
                    );
                } catch (err) {
                    handleError('NPI lookup error.', err);
                }
            }}
            disabled={hasPracticeNpi || isReadOnly}
        />
        <TextInputFormik label="Organization Name" id="organization_name" disabled={isReadOnly} />
        <TextInputFormik label="First Name" id="first_name" required disabled={isReadOnly} />
        <TextInputFormik label="Last Name" id="last_name" required disabled={isReadOnly} />
        <PhoneInputFormik
            label="Phone Number"
            id="telephone_number"
            required
            disabled={isReadOnly}
        />
        <PhoneInputFormik label="Fax Number" id="fax_number" disabled={isReadOnly} />
        <SuggestSingleSelect
            label="Referral Assignment Communication Preference"
            id="referral_assignment_comm_preference"
            required
            values={ReferralAssignmentCommPreferenceOptions}
            selectedItem={ReferralAssignmentCommPreferenceOptions.find(
                ({ value }) => value === formik.values.referral_assignment_comm_preference,
            )}
            onChange={(newVal) =>
                formik.setFieldValue('referral_assignment_comm_preference', newVal?.value)
            }
            disabled={isReadOnly}
        />
        <TextInputFormik label="Address Line 1" id="address_1" required disabled={isReadOnly} />
        <TextInputFormik label="Address Line 2" id="address_2" disabled={isReadOnly} />
        <TextInputFormik label="City" id="city" required disabled={isReadOnly} />
        <TextInputFormik label="State" id="state" required disabled={isReadOnly} />
        <TextInputFormik label="Postal Code" id="postal_code" required disabled={isReadOnly} />
        <TextInputFormik
            label="Taxonomy Code"
            id="taxonomy_code"
            required
            disabled={!isCreate || isReadOnly}
        />
        <TextInputFormik
            label="Classification"
            id="classification"
            required
            disabled={!isCreate || isReadOnly}
        />
        <TextInputFormik
            label="Specialization"
            id="specialization"
            disabled={!isCreate || isReadOnly}
        />
        {!isCreate && <TextInputFormik label="Last Update" id="last_update" disabled />}
    </>
);

PrescriberDetailsFormFields.propTypes = {
    formik: PropTypes.shape({
        values: PropTypes.shape({
            npi: PropTypes.string,
            practice_npi: PropTypes.string,
            referral_assignment_comm_preference: PropTypes.string,
        }).isRequired,
        setValues: PropTypes.func.isRequired,
        setFieldValue: PropTypes.func.isRequired,
    }).isRequired,
    isCreate: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    hasPracticeNpi: PropTypes.bool.isRequired,
};
